<template>
  <modal v-if="!loading" class="style-modal" @close-modal="close">
    <div
      v-if="filteredOfferSessions.length === 0"
      class="row col-12 game py-3 mx-0 my-2 justify-content-center bets-not-found"
    >
      No Open Offers
    </div>
    <div v-else class="container-fluid px-0 my-4">
      <div class="row player-chart-header justify-content-center">
        <!-- <div
            class="col-2 d-none d-md-flex justify-content-center flex-nowrap align-items-center pointer"
            @click="sortBy('start')"
          >
            START
          </div> -->
        <div
          class="col-md-7 col-10 justify-content-center flex-nowrap align-items-center bet-main-info pointer text-center"
          @click="sortBy('gameInfo')"
        >
          CANCEL ORDER - YOUR SIDE
        </div>
        <div
          class="col-1 d-none d-md-flex justify-content-center flex-nowrap align-items-center bet-risk pointer"
          @click="sortBy('risk')"
        >
          RISK
        </div>
        <div
          class="col-1 d-none d-md-flex justify-content-center flex-nowrap align-items-center bet-risk pointer"
          @click="sortBy('win')"
        >
          WIN
        </div>
        <div
          class="col-3 d-none d-md-flex justify-content-center flex-nowrap align-items-center bet-risk pointer"
          @click="sortBy('expiry')"
        >
          EXPIRY
        </div>
        <div
          class="col-2 d-flex d-md-none justify-content-center flex-nowrap align-items-center bet-risk pointer"
          @click="sortBy('expiry')"
        >
          RISK/WIN
        </div>
      </div>
      <div
        v-for="(session, index) in filteredOfferSessions"
        :key="index"
        class="row mx-0"
      >
        <my-offer
          :modal="true"
          :session="session"
          :type="'open-offers'"
          @cancel-offer="cancelOffer"
          @edit-order="editOrder"
        />
        <edit-order-modal
          v-if="showEditOfferModal"
          :session="editOrderSession"
          :full-game="editOrderGame"
          @cancel="cancelOffer"
          @close-modal="closeEditOrderModal"
        />
      </div>
      <div
        v-if="betPaginationCounter.nextAllowed"
        class="row mx-0 justify-content-center load-more pointer py-2"
        @click="loadMore"
      >
        {{ loadingMore ? 'Loading...' : 'Load More' }}
      </div>
    </div>
  </modal>
  <modal v-else class="modal-wrap" @close-modal="close">
    <spinner />
  </modal>
</template>
<script setup>
import Spinner from '@/components/global/Spinner.vue';
import Modal from '@/components/global/Modal.vue';
import MyOffer from '@/components/userBets/PendingOffer.vue';
import EditOrderModal from '@/components/betting/modals/EditOrderModal.vue';
import { useAPI } from '@/composables/useAPI';
import { emitter } from '@/core/emitter';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useUserStore } from '@/stores/user';
import { calculateWin, convertAmericanToDecimal } from '@/core/utils';
import { cancelOfferNotification } from '@/core/notifications';

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
});
const emit = defineEmits(['closeModal']);

const userStore = useUserStore();

const loading = ref(false);
const loadingMore = ref(false);
const sessions = ref([]);

const betPaginationCounter = ref({
  page: 1,
  limit: 25,
  numToSkip: 0,
  nextAllowed: true,
});

const activeSort = ref(null);

const OpenOKeys = ref({
  start: 0,
  gameInfo: 0,
  risk: 0,
  win: 0,
  expiry: 0,
});

const editOrderGame = ref(null);
const editOrderSession = ref(null);
const showEditOfferModal = ref(false);

const filteredSessions = computed(() => {
  return sessions.value.filter(
    (bet, betIx, self) =>
      self.findIndex(
        (t) => t.id === bet.id && t.createdAt === bet.createdAt,
      ) === betIx,
  );
});

const filteredOfferSessions = computed(() => {
  return filteredSessions.value.filter((el) => {
    const risk = el.bet * (1 - el.takenRatio);
    return calculateWin(risk, -el.odds) >= 0;
  });
});

onMounted(() => {
  fetchData();
  emitter.on('updateOffer', fetchData);
});

onBeforeUnmount(() => {
  emitter.off('updateOffer', fetchData);
});

async function fetchData() {
  try {
    loading.value = true;
    const { get } = useAPI('user/getOpenOffers/');
    const response = await get({
      gameID: props.gameId,
      numToSkip: betPaginationCounter.value.numToSkip,
    });
    sessions.value = response.open;
    betPaginationCounter.value.nextAllowed = response.allowNext;
    if (betPaginationCounter.value.nextAllowed) {
      betPaginationCounter.value.numToSkip = response.latestOrderIndex;
    }
  } catch (error) {
    console.log('error getting open orders');
  } finally {
    loading.value = false;
  }
}

// Appends the next page so users with more than one page of open offers on a
// game can reach every order (the aggregate book routes multi-order rows
// here, so all orders must be selectable).
async function loadMore() {
  if (loadingMore.value || !betPaginationCounter.value.nextAllowed) return;
  try {
    loadingMore.value = true;
    const { get } = useAPI('user/getOpenOffers/');
    const response = await get({
      gameID: props.gameId,
      numToSkip: betPaginationCounter.value.numToSkip,
    });
    const nextPage = Array.isArray(response.open) ? response.open : [];
    sessions.value = sessions.value.concat(nextPage);
    betPaginationCounter.value.nextAllowed = response.allowNext;
    if (response.allowNext) {
      betPaginationCounter.value.numToSkip = response.latestOrderIndex;
    }
  } catch (error) {
    console.log('error loading more open orders', error);
  } finally {
    loadingMore.value = false;
  }
}

function close() {
  emit('closeModal');
}

function sortBy(key) {
  activeSort.value = key;
  sortOpenOffers({ key });
}

function sortOpenOffers({ key, defaultsort = false }) {
  switch (key) {
    case 'start':
      if (!defaultsort) OpenOKeys.value.start += 1;
      sessions.value.sort((a, b) =>
        compare(new Date(a.game.start), new Date(b.game.start)),
      );
      if (OpenOKeys.value.start % 2 !== 0) {
        sessions.value.reverse();
      }
      break;
    case 'gameInfo':
      if (!defaultsort) OpenOKeys.value.gameInfo += 1;
      sessions.value.sort((a, b) => {
        const adata = !a.game.isFutures
          ? a.game.participants[0].longName
          : a.game.eventName;
        const bdata = !b.game.isFutures
          ? b.game.participants[0].longName
          : b.game.eventName;
        return compare(adata, bdata);
      });
      if (OpenOKeys.value.gameInfo % 2 !== 0) {
        sessions.value.reverse();
      }
      break;
    case 'risk':
      if (!defaultsort) OpenOKeys.value.risk += 1;
      sessions.value.sort((a, b) =>
        compare(a.bet.toFixed(0), b.bet.toFixed(0)),
      );
      if (OpenOKeys.value.risk % 2 !== 0) {
        sessions.value.reverse();
      }
      break;
    case 'win':
      if (!defaultsort) OpenOKeys.value.win += 1;
      sessions.value.sort((a, b) =>
        compare(
          calculateWin(a.bet, a.odds).toFixed(0),
          calculateWin(b.bet, b.odds).toFixed(0),
        ),
      );
      if (OpenOKeys.value.win % 2 !== 0) {
        sessions.value.reverse();
      }
      break;
    case 'expiry':
      if (!defaultsort) OpenOKeys.value.expiry += 1;
      sessions.value.sort((a, b) =>
        compare(getExpirySortDate(a), getExpirySortDate(b)),
      );
      if (OpenOKeys.value.expiry % 2 !== 0) {
        sessions.value.reverse();
      }
      break;
    default:
      break;
  }
}

function compare(a, b) {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

function getExpirySortDate(session) {
  const useGameStart = session.gameStartExpiry === true || !session.expiry;
  const sourceDate = useGameStart ? session.game?.start : session.expiry;
  const parsed = new Date(sourceDate);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
}

/*
function trackCancelIndividualOrder(session) {
  const eventData = {
    event: 'cancel_individual_order',
    sessionId: session.id,
    gameId: session.game.id,
    eventName: session.game.eventName,
    type: session.type,
    risk: session.bet,
    sumUntaken: session.remaining,
    odds: session.odds,
  };
  GAUtils.cancelIndividualOrder(eventData);
}
*/
function resetBetPagination() {
  betPaginationCounter.value.page = 1;
  betPaginationCounter.value.numToSkip = 0;
  betPaginationCounter.value.nextAllowed = true;
}
async function cancelOffer(session) {
  try {
    loading.value = true;
    const { request } = useAPI('session/cancel');
    const response = await request({
      sessionID: session.id,
    });
    const { description, remaining, odds } = response;
    cancelOfferNotification(description, remaining, odds);
    resetBetPagination();
    await fetchData();
  } catch (error) {
    emitter.emit('showAlert', {
      type: 'canceled-order',
      shown: true,
      status: 'danger',
      title: 'Order Not Canceled. Please Try Again',
      detail: '',
    });
    resetBetPagination();
    fetchData();
  } finally {
    loading.value = false;
  }
}

function getFlippedSession(session) {
  return {
    id: session.id,
    bet: session.bet,
    createdBy: userStore.user.id,
    expiry: session.expiry,
    gameStartExpiry:
      session.gameStartExpiry === true || !session.expiry,
    sumUntaken: session.bet * (convertAmericanToDecimal(-session.odds) - 1),
    type: session.type,
    odds: -session.odds,
    spread: session.type === 'spread' ? -session.spread : null,
    total: session.type === 'total' ? session.total : null,
    OU:
      session.type === 'total'
        ? ['over', 'under'].find((ou) => ou !== session.OU)
        : null,
    participantID: session.type === 'total' ? null : session.otherParticipantID,
    takenRatio: session.takenRatio,
    market: session.market,
    side: session.side === 'yes' ? 'no' : 'yes',
  };
}

function editOrder(payload) {
  editOrderGame.value = payload.game;
  editOrderSession.value = getFlippedSession(payload);
  showEditOfferModal.value = true;
}

function closeEditOrderModal() {
  showEditOfferModal.value = false;
  editOrderGame.value = {};
  editOrderSession.value = {};
  // fetchData advances the pagination cursor when more pages exist; without
  // a reset the refetch would REPLACE the list with the next page and the
  // just-edited order would vanish.
  resetBetPagination();
  fetchData();
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/tokens.scss' as main;

.player-chart-header {
  font-weight: main.$font-weight-semibold;
  color: main.$blue-1100;
  border-radius: 4px;
  font-size: small;
  margin: 0;
  width: 100%;
  box-shadow: 0 2px 4px 0 main.$blue-1200;
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: main.$font-size-xs;
    border-radius: 0px;
    color: main.$grey-2400;
  }
}

.bets-not-found {
  color: main.$grey-2400;
  background: main.$grey-500;
  font-weight: main.$font-weight-normal;
  font-size: main.$font-size-sm;
  text-align: center;
  border-radius: 4px;
}

.load-more {
  font-size: main.$font-size-sm;
  font-weight: main.$font-weight-normal;
  text-decoration: underline;
}
</style>
